import { useEffect, useRef } from 'react';

/**
 * GoogleSignInButton — loads Google's GSI script on mount and renders the
 * standard "Sign in with Google" button into a host div. On credential,
 * hands the raw ID token JWT up to the caller (POSTed to
 * /api/admin/auth/google, verified server-side).
 */

type GoogleCredentialResponse = { credential: string };
type GoogleAccountsId = {
  initialize: (opts: {
    client_id: string;
    callback: (r: GoogleCredentialResponse) => void;
    ux_mode: 'popup';
  }) => void;
  renderButton: (
    el: HTMLElement,
    opts: { theme: 'outline'; size: 'large'; width: number; text: 'signin_with' },
  ) => void;
};
type GoogleWindow = Window & { google?: { accounts: { id: GoogleAccountsId } } };

const GSI_SRC = 'https://accounts.google.com/gsi/client';

function loadGsi(): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
  if (existing && (window as GoogleWindow).google) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = existing ?? document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load Google sign-in.'));
    if (!existing) document.head.appendChild(s);
  });
}

export function GoogleSignInButton({
  clientId,
  onCredential,
  onError,
}: {
  clientId: string;
  onCredential: (credential: string) => void;
  onError: (message: string) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let cancelled = false;
    loadGsi()
      .then(() => {
        const id = (window as GoogleWindow).google?.accounts.id;
        if (cancelled || !id || !host.current) return;
        id.initialize({ client_id: clientId, callback: (r) => onCredential(r.credential), ux_mode: 'popup' });
        // A re-render that changes onCredential/onError re-runs this effect;
        // clear the host first so a second renderButton() call never stacks a
        // duplicate button next to the first.
        host.current.replaceChildren();
        id.renderButton(host.current, { theme: 'outline', size: 'large', width: 280, text: 'signin_with' });
      })
      .catch((e: unknown) => onError(e instanceof Error ? e.message : 'Could not load Google sign-in.'));
    return () => {
      cancelled = true;
    };
  }, [clientId, onCredential, onError]);
  return <div ref={host} data-testid="google-signin" className="flex justify-center" />;
}
